/**
 * useMentionsFeed - "Luke mentioned you on Jane Smith": the corner card of an unread @mention, or
 * of a reply to a note you wrote (docs/plans/crm-report-builder-search-collab-plan.md A3.3, A3.4
 * and B3.4).
 *
 * NO POLL OF ITS OWN, and no stream (docs/owner-actions.md section 39: every stream holds a server
 * slot). The nav counts already ask GET /worklist?counts=true every 60 s, and that answer carries
 * `buckets.mentions`: how many unread mentions I have and the newest one's { id, created_at }.
 * This feed reads that same answer through the shared nav counts (the outreach card's pattern,
 * useOutreachFeed.ts). Only when the newest unread mention is one it has never carded does it ask
 * GET /mentions?status=unread&limit=3, once, for the words of the new cards.
 *
 * One card per mention, per person, ever. What has been carded is kept in localStorage under a
 * per-user key (crm.mentionCards.v1:<userId>, like outreachNotice.ts), so a reload or the next
 * count never raises the same card twice, a second tab of mine skips a card the first one already
 * raised (the store is read again before any request), and a shared front-desk browser keeps one
 * recruiter's cards apart from another's. A mention withdrawn by a teammate's edit and restored by
 * a later one comes back with a new time and is told again (the key carries the time).
 *
 * A card lapses after DISMISS_AFTER_MENTION_MS on screen (overlayQueue.ts); the Mentions lane on
 * Today keeps the mention until it is read. Open goes to the lead on that note and marks the
 * mention read; the next count clears the badge, the lane, and the card of a mention read
 * anywhere else. No OS notification: the card and the lane are the whole of decision M3 a.
 *
 * The lead page's half lives here too: useMarkLeadMentionsRead (opening a lead marks my unread
 * mentions on it read, once per visit, decision M4 a, driven by the same counts) and
 * useNoteLinkFocus (the `?note=<id>` of the card's Open scrolls the thread to that note).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { notifyStatsChanged, useTeam } from '../../lib';
import { useSharedNavCounts } from '../../useNavCounts';
import type { WorklistMentionsBucket } from '../../types';
import { listMentions, markMentionsRead } from '../../mentions/api';
import { MENTION_CARD_PREFIX, cardFromMention, noteParamOf, type MentionCard } from '../../mentions/mentionLogic';
import type { MentionItem } from '../../mentions/types';

export type { MentionCard };

/** How many unread mentions one new-mention read asks for (plan B3.4). */
export const MENTION_CARD_FETCH_LIMIT = 3;

/** Carded keys kept per person. Far beyond the mentions anyone has unread at once. */
export const MENTION_CARDS_CAP = 200;

/** The per-user localStorage key of the carded set (per user, like crm.outreachAssigned.v1). */
export const mentionCardsKey = (userId: string): string => `crm.mentionCards.v1:${userId}`;

export interface MentionCardStore {
  /** The mentionCardKey of every mention that has had its card, oldest first. */
  carded: string[];
}

const EMPTY_STORE: MentionCardStore = { carded: [] };

/** Tolerant: junk, an old shape or nothing at all is an empty store. */
export function parseMentionCardStore(raw: string | null | undefined): MentionCardStore {
  if (!raw) return { carded: [] };
  try {
    const parsed = JSON.parse(raw);
    const list: unknown[] = Array.isArray(parsed?.carded) ? parsed.carded : [];
    return { carded: list.filter((x): x is string => typeof x === 'string' && x !== '') };
  } catch {
    return { carded: [] };
  }
}

/** Newest wins past the cap: slice from the END of insertion order. */
export function serializeMentionCardStore(s: MentionCardStore): string {
  return JSON.stringify({ carded: s.carded.slice(-MENTION_CARDS_CAP) });
}

/**
 * The key a mention is carded under: its id and when it was made, to the millisecond. A mention
 * withdrawn by an edit and restored by a later one keeps its id but gets a new time
 * (crmMentions.syncMentionsAfterEdit), so it is carded again; an unreadable time falls back to
 * the id alone.
 */
export function mentionCardKey(id: string, createdAt: string | null | undefined): string {
  const t = createdAt ? Date.parse(createdAt) : NaN;
  return Number.isFinite(t) ? `${id}@${t}` : id;
}

/** Add keys, keeping insertion order and no duplicates; the same store back when nothing is new. */
export function withCarded(s: MentionCardStore, keys: ReadonlyArray<string>): MentionCardStore {
  const have = new Set(s.carded);
  const added: string[] = [];
  for (const key of keys) {
    if (!key || have.has(key)) continue;
    have.add(key);
    added.push(key);
  }
  return added.length ? { carded: [...s.carded, ...added] } : s;
}

/**
 * The newest unread mention's key when it has not had a card yet: the one moment the feed reads
 * GET /mentions. Null for an unknown bucket (an older api, a failed count), for nothing unread,
 * for a bucket without `newest`, and for a newest mention already carded.
 */
export function uncardedNewestKey(
  bucket: WorklistMentionsBucket | null | undefined,
  store: MentionCardStore,
): string | null {
  if (!bucket || bucket.count <= 0 || !bucket.newest) return null;
  const key = mentionCardKey(bucket.newest.id, bucket.newest.created_at);
  return store.carded.includes(key) ? null : key;
}

/**
 * The card rows still worth showing after a new count. An unknown count keeps them; nothing
 * unread clears them all; otherwise a card made after the newest unread mention has been read or
 * withdrawn since, so it goes (a read card older than the newest just lapses on its budget). The
 * same array comes back when nothing changes.
 */
export function pruneMentionItems(
  items: MentionItem[],
  bucket: WorklistMentionsBucket | null | undefined,
): MentionItem[] {
  if (!bucket || !items.length) return items;
  if (bucket.count <= 0 || bucket.newest === null) return [];
  if (!bucket.newest) return items;
  const newestAt = Date.parse(bucket.newest.created_at);
  if (!Number.isFinite(newestAt)) return items;
  const kept = items.filter((item) => {
    const t = Date.parse(item.created_at);
    return !Number.isFinite(t) || t <= newestAt;
  });
  return kept.length === items.length ? items : kept;
}

/**
 * The rows of a GET /mentions?status=unread answer that get a new card: unread, openable (a card
 * can be built from them) and not carded yet, in the answer's order (newest first).
 */
export function freshMentionItems(
  items: ReadonlyArray<MentionItem>,
  store: MentionCardStore,
): MentionItem[] {
  const carded = new Set(store.carded);
  return items.filter(
    (item) => !item.read_at && cardFromMention(item) !== null && !carded.has(mentionCardKey(item.id, item.created_at)),
  );
}

/** Add card rows; a row with the same mention id replaces the old one (a restored mention). */
export function upsertMentionItems(items: MentionItem[], fresh: ReadonlyArray<MentionItem>): MentionItem[] {
  if (!fresh.length) return items;
  const ids = new Set(fresh.map((m) => m.id));
  return [...fresh, ...items.filter((m) => !ids.has(m.id))];
}

// localStorage can throw (private windows, blocked site data). The cost of a failure is one more
// card, never a broken corner.
function readStore(userId: string | null): MentionCardStore {
  if (!userId) return EMPTY_STORE;
  try {
    return parseMentionCardStore(window.localStorage.getItem(mentionCardsKey(userId)));
  } catch {
    return EMPTY_STORE;
  }
}

function writeStore(userId: string | null, s: MentionCardStore): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(mentionCardsKey(userId), serializeMentionCardStore(s));
  } catch {
    /* the in-memory store still holds for this session */
  }
}

export interface MentionsFeed {
  cards: MentionCard[];
  /** Take a card off the corner (its X, or its budget ran out). It never comes back. */
  dismiss: (id: string) => void;
  /** The card's Open: the lead, scrolled to that note, and the mention marked read. */
  open: (card: MentionCard) => void;
}

export function useMentionsFeed(): MentionsFeed {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mentions: bucket } = useSharedNavCounts();
  const { byId } = useTeam();
  const userId = user?.id ?? null;

  const [store, setStore] = useState<MentionCardStore>(() => readStore(userId));
  const [items, setItems] = useState<MentionItem[]>([]);

  // Read inside the one request's answer, which may land after newer counts and a newer store.
  const storeRef = useRef(store);
  storeRef.current = store;
  const bucketRef = useRef(bucket);
  bucketRef.current = bucket;
  const userRef = useRef(userId);
  userRef.current = userId;
  // The key whose words are being (or were just) read, so a re-render never asks twice.
  const askedRef = useRef<string | null>(null);
  // Whose store is loaded (the initial state read it), so only a real change of person reloads.
  const storeUserRef = useRef(userId);

  // A different person signing in on this browser has their own carded set, and none of my cards.
  useEffect(() => {
    if (storeUserRef.current === userId) return;
    storeUserRef.current = userId;
    const next = readStore(userId);
    storeRef.current = next;
    setStore(next);
    setItems([]);
    askedRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setItems((prev) => pruneMentionItems(prev, bucket));
    const key = uncardedNewestKey(bucket, store);
    if (!key || askedRef.current === key) return;
    // Another tab of mine may have carded it since this one read the store.
    const persisted = readStore(userId);
    if (persisted.carded.includes(key)) {
      const merged = withCarded(store, persisted.carded);
      storeRef.current = merged;
      setStore(merged);
      return;
    }
    askedRef.current = key;
    const asker = userId;
    // Not cancelled on cleanup: the answer is applied through refs and guarded by who asked, so
    // a re-run (or a StrictMode double effect) cannot drop the card it was fetched for.
    listMentions({ status: 'unread', limit: MENTION_CARD_FETCH_LIMIT })
      .then(({ mentions }) => {
        if (userRef.current !== asker) return;
        // What another tab of mine carded meanwhile counts too: neither tab raises the other's
        // cards again, and neither write loses the other's keys.
        const before = storeRef.current;
        const current = withCarded(before, readStore(asker).carded);
        const fresh = freshMentionItems(mentions, current);
        // The newest key is recorded even when its row was read in the meantime, so the same
        // count can never send the request again.
        const next = withCarded(current, [...fresh.map((m) => mentionCardKey(m.id, m.created_at)), key]);
        if (next !== current) writeStore(asker, next);
        if (next !== before) {
          storeRef.current = next;
          setStore(next);
        }
        if (fresh.length) {
          setItems((prev) => pruneMentionItems(upsertMentionItems(prev, fresh), bucketRef.current));
        }
      })
      .catch(() => {
        // Nothing was carded, so the next count (the one poll's next tick) asks again.
        if (askedRef.current === key) askedRef.current = null;
      });
  }, [userId, bucket, store]);

  const nameOf = useCallback((id: string): string | null => byId.get(id)?.name ?? null, [byId]);

  // Built at render, so the author's name fills in when the team list arrives; the card id is
  // stable, so the overlay queue keeps each card's served time across the rebuild.
  const cards = useMemo<MentionCard[]>(() => {
    const out: MentionCard[] = [];
    for (const item of items) {
      const card = cardFromMention(item, { nameOf });
      if (card) out.push(card);
    }
    return out;
  }, [items, nameOf]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((m) => `${MENTION_CARD_PREFIX}${m.id}` !== id);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const open = useCallback(
    (card: MentionCard) => {
      dismiss(card.id);
      navigate(card.href);
      markMentionsRead({ ids: [card.mentionId] })
        .then(() => notifyStatsChanged())
        .catch(() => {
          /* the lead page marks its mentions read on opening anyway (useMarkLeadMentionsRead) */
        });
    },
    [dismiss, navigate],
  );

  return { cards, dismiss, open };
}

/**
 * Opening a lead marks my unread mentions on its notes read (decision M4 a; plan B3.4): ONE
 * POST /mentions/read { contact_id } per visit to a lead, and only when the shared counts say I
 * have unread mentions at all, so opening leads all day costs nothing while I have none. When the
 * lead opens before the first count has arrived, the decision waits for it; after that it is made
 * once for the visit, so a mention that arrives while I am on the lead still gets its card. The
 * badge, the lane and the card refresh on the one count poll when anything was marked.
 */
export function useMarkLeadMentionsRead(contactId: string | null | undefined): void {
  const { mentions } = useSharedNavCounts();
  const unread = mentions ? mentions.count > 0 : null;
  const decidedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!contactId || unread === null || decidedRef.current === contactId) return;
    decidedRef.current = contactId;
    if (!unread) return;
    markMentionsRead({ contact_id: contactId })
      .then(({ updated }) => {
        if (updated > 0) notifyStatsChanged();
      })
      .catch(() => {
        /* best effort: the card's Open and the lane's Mark all read still work */
      });
  }, [contactId, unread]);
}

/** How long a `?note=` link waits for its note to render in the thread before giving up. */
export const NOTE_FOCUS_WAIT_MS = 15_000;

/**
 * The lead page's half of a mention's Open (plan B3.4): `?note=<id>` in the address names a note
 * to scroll to and flash. The thread renders after the lead loads (`ready`), so this waits, at
 * most NOTE_FOCUS_WAIT_MS, for the note's row (`#comm-<id>`, CommsThread's row id) and then calls
 * `onFocus(noteId)` once per link followed; a new link to the same note (a new `linkKey`, the
 * router location's key) calls it again. A note outside the thread's loaded page never renders,
 * and then nothing happens. The wait is one bounded timeout per link, never a poll.
 */
export function useNoteLinkFocus(
  search: string,
  linkKey: string,
  ready: boolean,
  onFocus: (noteId: string) => void,
): void {
  const noteId = noteParamOf(search);
  const doneRef = useRef<string | null>(null);
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  useEffect(() => {
    if (!noteId || !ready) return undefined;
    const token = `${linkKey}:${noteId}`;
    if (doneRef.current === token) return undefined;
    const rowId = `comm-${noteId}`;
    const fire = () => {
      doneRef.current = token;
      onFocusRef.current(noteId);
    };
    if (document.getElementById(rowId)) {
      fire();
      return undefined;
    }
    if (typeof MutationObserver === 'undefined') return undefined;
    let timer = 0;
    const observer = new MutationObserver(() => {
      if (!document.getElementById(rowId)) return;
      observer.disconnect();
      window.clearTimeout(timer);
      fire();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    timer = window.setTimeout(() => observer.disconnect(), NOTE_FOCUS_WAIT_MS);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [noteId, linkKey, ready]);
}

export default useMentionsFeed;
